import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { getGraphData } from '../../../../api/graph';
import { auth, analytics } from '../../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { usePanelState } from '../../LeftPanel/panelState';
import { DEFAULT_SELECTIONS } from './config';
import { getChartOptions } from './utils';
import { usePriceLines } from './hooks/usePriceLines';
import { useTradePriceLines } from './hooks/useTradePriceLines';
import { useChartTradeCreation } from './hooks/useChartTradeCreation';
import { convertDataToTimezone, getLocalTimezone } from './utils/timezoneUtils';
import ChartLegend from './components/ChartLegend';
import PriceLinesList from './components/PriceLinesList';
import ChartToolbar from './components/ChartToolbar';
import PriceHeader from './components/PriceHeader';
import TimezoneSelector from './components/TimezoneSelector';
import ChartError from '../ChartError';
import ChartSkeleton from '../ChartSkeleton';
import logger from '../../../../utils/logger';
import './TradingViewLightChart.css';

const TradingViewLightChart = forwardRef(({
    darkMode,
    selectedCategory,
    selectedInstrument,
    onChartUpdate,
    onPriceUpdate,
    lines = [],
    isChatExpanded,
    isExpandedLeftPanel
}, ref) => {
    const container = useRef();
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const lastDataRef = useRef(null);
    const originalDataRef = useRef(null);
    const isInitialRenderRef = useRef(true);
    const prevInstrumentRef = useRef(null);

    // Counter that increments each time chart is initialized - used to trigger re-subscription
    // This is needed because refs don't trigger re-renders
    const [chartInstanceId, setChartInstanceId] = useState(0);

    // State
    const [legendData, setLegendData] = useState(null);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [previousPrice, setPreviousPrice] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isControlsDisabled, setIsControlsDisabled] = useState(false);
    const [chartError, setChartError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const loadingStartTimeRef = useRef(null);
    const progressIntervalRef = useRef(null);

    // Timezone state
    const [selectedTimezone, setSelectedTimezone] = useState(() => {
        const savedTimezone = localStorage.getItem('ntb:copilot:timezone');
        return savedTimezone ? JSON.parse(savedTimezone) : getLocalTimezone();
    });

    const [categorySelections, setCategorySelections] = useState(() => {
        const savedSelections = localStorage.getItem('ntb:copilot:categorySelections');
        return savedSelections ? JSON.parse(savedSelections) : DEFAULT_SELECTIONS;
    });

    const [selectedTimeframe, setSelectedTimeframe] = useState(() => {
        const savedTimeframe = localStorage.getItem('ntb:copilot:timeframe');
        return savedTimeframe || 'M15' || categorySelections[selectedCategory]?.timeframe || DEFAULT_SELECTIONS[selectedCategory].timeframe;
    });

    const { isExpanded } = usePanelState();

    // Price lines hook (for AI annotations)
    const {
        priceLines,
        createPriceLine,
        removePriceLine,
        deleteAllLines
    } = usePriceLines(selectedInstrument, candleSeriesRef);

    // ConceptGate trade lines (entry, stop, target) with drag-to-edit
    const {
        isDragging: isDraggingTradeLine,
        draggingLine,
        hasTradeLines,
    } = useTradePriceLines(candleSeriesRef, chartRef, container, !isLoading);

    // Chart trade creation (click to set entry/stop/target)
    // Pass chartInstanceId to trigger re-subscription when chart is re-initialized
    const {
        isCreating: isCreatingTrade,
        startCreation: startTradeCreation,
        cancelCreation: cancelTradeCreation,
        getStepInstruction,
    } = useChartTradeCreation(chartRef, candleSeriesRef, !isLoading, chartInstanceId);

    // Data fetching
    const fetchGraphData = useCallback(async (instrument, timeframe, count, source) => {
        return new Promise((resolve, reject) => {
            getGraphData(auth, (data) => {
                if (data && Array.isArray(data) && data.length > 0) {
                    const formattedData = data.map(candle => ({
                        time: candle.time,
                        open: parseFloat(candle.mid.o),
                        high: parseFloat(candle.mid.h),
                        low: parseFloat(candle.mid.l),
                        close: parseFloat(candle.mid.c),
                        volume: candle.volume
                    }));

                    originalDataRef.current = [...formattedData];
                    const adjustedData = convertDataToTimezone(formattedData, selectedTimezone);
                    resolve(adjustedData);
                } else {
                    logger.error("Received invalid data:", data);
                    reject(new Error("Failed to fetch forex data: Invalid or empty data received"));
                }
            }, instrument, timeframe, count, source);
        });
    }, [selectedTimezone]);

    const fetchData = useCallback(async (instrument, timeframe, dataCount = 300) => {
        if (selectedCategory === 'FOREX') {
            return fetchGraphData(instrument, timeframe, dataCount, "oanda");
        } else if (selectedCategory === 'INDICES') {
            return fetchGraphData(instrument, timeframe, dataCount, "oanda");
        } else if (selectedCategory === 'CRYPTO') {
            return fetchGraphData(instrument, timeframe, dataCount, "binance");
        } else if (selectedCategory === 'STOCKS') {
            return fetchGraphData(instrument, timeframe, dataCount, "yahoo");
        }
    }, [selectedCategory, fetchGraphData]);

    const fetchLatestData = useCallback(async (instrument, timeframe, count = 30) => {
        if (!instrument || !timeframe) return null;
        return fetchData(instrument, timeframe, count);
    }, [fetchData]);

    // Update chart data
    const updateChartData = useCallback(async (newData) => {
        if (!newData || !Array.isArray(newData) || newData.length === 0 || !candleSeriesRef.current) {
            return false;
        }

        try {
            for (const dataPoint of newData) {
                if (candleSeriesRef.current) {
                    candleSeriesRef.current.update(dataPoint);
                }
            }

            if (newData.length > 0) {
                const lastPoint = newData[newData.length - 1];
                lastDataRef.current = lastPoint;

                // Update current price
                setPreviousPrice(currentPrice);
                setCurrentPrice(lastPoint.close);
            }

            return true;
        } catch (error) {
            logger.error("Error updating chart data:", error);
            return false;
        }
    }, [currentPrice]);

    // Timeframe change
    const handleTimeframeChange = (timeframe) => {
        setSelectedTimeframe(timeframe);
        setCategorySelections(prev => ({
            ...prev,
            [selectedCategory]: {
                ...prev[selectedCategory],
                timeframe
            }
        }));
    };

    // Fullscreen toggle
    const handleFullscreenToggle = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    // Initialize chart
    const initializeChart = useCallback((instrument, timeframe) => {
        if (!instrument || !timeframe) return;
        setChartError(null);
        setIsLoading(true);
        setLoadingProgress(0);
        setIsControlsDisabled(true);
        loadingStartTimeRef.current = Date.now();

        progressIntervalRef.current = setInterval(() => {
            const elapsed = Date.now() - loadingStartTimeRef.current;
            if (elapsed > 2000) {
                const progress = Math.min(90, 30 + (elapsed - 2000) / 50);
                setLoadingProgress(progress);
            }
        }, 100);

        if (container.current) {
            container.current.innerHTML = '';
        }

        import('lightweight-charts').then(({ createChart }) => {
            try {
                if (!container.current) return;

                const chartInstance = createChart(container.current, {
                    ...getChartOptions(darkMode, container.current),
                    layout: {
                        ...getChartOptions(darkMode, container.current).layout,
                        background: {
                            type: 'solid',
                            color: darkMode ? '#131722' : '#ffffff'
                        },
                    },
                });

                // Clean candlestick series
                const candleSeries = chartInstance.addCandlestickSeries({
                    upColor: '#26a69a',
                    downColor: '#ef5350',
                    borderVisible: false,
                    wickUpColor: '#26a69a',
                    wickDownColor: '#ef5350',
                    priceFormat: {
                        type: 'price',
                        precision: 5,
                        minMove: 0.00001,
                    },
                    barSpacing: 8,
                    baseLineVisible: true,
                    lastValueVisible: true,
                });
                candleSeriesRef.current = candleSeries;

                // Use passed parameters instead of state
                fetchData(instrument, timeframe, 300)
                    .then(data => {
                        setChartError(null);
                        setLoadingProgress(80);
                        candleSeries.setData(data);

                        if (data.length > 0) {
                            const lastPoint = data[data.length - 1];
                            const firstPoint = data[0];
                            lastDataRef.current = lastPoint;
                            setCurrentPrice(lastPoint.close);
                            setPreviousPrice(firstPoint.close);
                        }

                        const logicalRange = {
                            from: Math.max(0, data.length - 100),
                            to: data.length + 50
                        };
                        chartInstance.timeScale().setVisibleLogicalRange(logicalRange);

                        setLoadingProgress(100);
                        setTimeout(() => {
                            setIsLoading(false);
                            setIsControlsDisabled(false);
                            // Increment chartInstanceId to trigger re-subscription in hooks
                            setChartInstanceId(prev => prev + 1);
                            if (progressIntervalRef.current) {
                                clearInterval(progressIntervalRef.current);
                                progressIntervalRef.current = null;
                            }
                        }, 300);
                    })
                    .catch(error => {
                        logger.error("Error fetching data:", error);
                        setChartError(error);
                        setIsLoading(false);
                        setIsControlsDisabled(false);
                        if (progressIntervalRef.current) {
                            clearInterval(progressIntervalRef.current);
                            progressIntervalRef.current = null;
                        }
                    });

                chartRef.current = chartInstance;

                const handleResize = () => {
                    setTimeout(() => {
                        if (container.current && chartInstance) {
                            chartInstance.applyOptions({
                                width: container.current.clientWidth,
                                height: container.current.clientHeight,
                            });
                        }
                    }, 100);
                };

                window.addEventListener('resize', handleResize);

                // Crosshair move for legend
                chartInstance.subscribeCrosshairMove(param => {
                    if (param.time) {
                        const data = param.seriesData.get(candleSeries);
                        if (data) {
                            setLegendData({
                                time: param.time,
                                open: data.open,
                                high: data.high,
                                low: data.low,
                                close: data.close,
                                direction: data.close >= data.open ? 'up' : 'down'
                            });
                        }
                    } else {
                        setLegendData(null);
                    }
                });

                return () => {
                    window.removeEventListener('resize', handleResize);
                    if (chartRef.current) {
                        chartRef.current.remove();
                    }
                };
            } catch (error) {
                logger.error("Error creating chart:", error);
                setChartError(error);
                setIsLoading(false);
                setIsControlsDisabled(false);
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                }
            }
        }).catch(error => {
            logger.error("Error loading lightweight-charts:", error);
            setChartError(error);
            setIsLoading(false);
            setIsControlsDisabled(false);
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
        });
    }, [darkMode, fetchData]);

    // Clear errors on change
    useEffect(() => {
        setChartError(null);
    }, [selectedInstrument, selectedTimeframe]);

    // Propagate current price to parent
    useEffect(() => {
        if (onPriceUpdate && currentPrice !== null) {
            onPriceUpdate(currentPrice);
        }
    }, [currentPrice, onPriceUpdate]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    // Refresh handler
    const handleRefresh = useCallback(async (options = {}) => {
        const { skipFullRefresh = false } = options;

        setIsControlsDisabled(true);

        let savedVisibleRange = null;
        try {
            if (chartRef.current) {
                savedVisibleRange = chartRef.current.timeScale().getVisibleRange();
            }
        } catch (error) {
            logger.error("Error saving visible range:", error);
        }

        if (candleSeriesRef.current && lastDataRef.current) {
            try {
                const newData = await fetchLatestData(selectedInstrument, selectedTimeframe, 10);

                if (newData && newData.length > 0) {
                    const lastExistingTime = lastDataRef.current.time;
                    const updatedLastCandle = newData.find(candle => candle.time === lastExistingTime);

                    if (updatedLastCandle) {
                        updateChartData([updatedLastCandle]);
                    }

                    const newerData = newData.filter(candle => candle.time > lastExistingTime);

                    if (newerData.length > 0) {
                        logger.log(`Adding ${newerData.length} new data points`);
                        updateChartData(newerData);

                        if (savedVisibleRange && chartRef.current) {
                            try {
                                const currentRange = chartRef.current.timeScale().getVisibleLogicalRange();
                                if (currentRange) {
                                    chartRef.current.timeScale().setVisibleLogicalRange({
                                        from: currentRange.from + newerData.length,
                                        to: currentRange.to + newerData.length
                                    });
                                }
                            } catch (error) {
                                logger.error("Error adjusting visible range:", error);
                            }
                        }

                        setIsControlsDisabled(false);
                        return true;
                    } else if (updatedLastCandle) {
                        logger.log('Updated last candle with fresh data');
                        setIsControlsDisabled(false);
                        return true;
                    } else {
                        if (!skipFullRefresh) {
                            try {
                                const fullData = await fetchData(selectedInstrument, selectedTimeframe, 300);
                                if (fullData && fullData.length > 0 && candleSeriesRef.current) {
                                    candleSeriesRef.current.setData(fullData);

                                    if (fullData.length > 0) {
                                        lastDataRef.current = fullData[fullData.length - 1];
                                        setCurrentPrice(fullData[fullData.length - 1].close);
                                    }

                                    logger.log('Updated chart with new timeframe data');
                                    setIsControlsDisabled(false);
                                    return true;
                                }
                            } catch (error) {
                                logger.error("Error updating timeframe data:", error);
                                setIsControlsDisabled(false);
                            }
                        }
                    }
                }
            } catch (error) {
                logger.error("Error fetching incremental data:", error);
                setIsControlsDisabled(false);
            }
        }

        if (!skipFullRefresh) {
            logger.log('Falling back to full refresh');
            setTimeout(() => {
                const preservingInitialize = async () => {
                    await initializeChart(selectedInstrument, selectedTimeframe);
                    setIsControlsDisabled(false);

                    if (savedVisibleRange && chartRef.current) {
                        setTimeout(() => {
                            try {
                                chartRef.current.timeScale().setVisibleRange(savedVisibleRange);
                            } catch (error) {
                                logger.error("Error restoring visible range:", error);
                            }
                        }, 100);
                    }
                };

                preservingInitialize();
            }, 100);
        } else {
            setIsControlsDisabled(false);
        }
    }, [selectedInstrument, selectedTimeframe, fetchLatestData, fetchData, updateChartData, initializeChart]);

    // Effects for initialization

    // Effect 1: Instrument change -> Full Re-Init
    useEffect(() => {
        if (selectedInstrument && selectedTimeframe) {
            // Only re-initialize if instrument changed or it's the very first render
            if (prevInstrumentRef.current !== selectedInstrument || isInitialRenderRef.current) {
                prevInstrumentRef.current = selectedInstrument;
                initializeChart(selectedInstrument, selectedTimeframe);
                isInitialRenderRef.current = false;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstrument, initializeChart]); // selectedTimeframe intentionally excluded - handled by Effect 2

    // Effect 2: Timeframe change -> Full Re-Init (different candle data)
    // Track previous timeframe to detect actual changes
    const prevTimeframeRef = useRef(selectedTimeframe);

    useEffect(() => {
        // Skip if this is the first render (handled by Effect 1)
        if (isInitialRenderRef.current) return;

        // Only act if timeframe actually changed
        if (prevTimeframeRef.current !== selectedTimeframe && selectedInstrument && selectedTimeframe) {
            prevTimeframeRef.current = selectedTimeframe;
            // Timeframe change requires full re-init since candle data is completely different
            initializeChart(selectedInstrument, selectedTimeframe);
        }
    }, [selectedTimeframe, selectedInstrument, initializeChart]);

    useEffect(() => {
        if (onChartUpdate && selectedInstrument && selectedTimeframe && selectedCategory) {
            onChartUpdate(selectedInstrument, selectedTimeframe, selectedCategory);
        }
    }, [selectedInstrument, selectedTimeframe, selectedCategory, onChartUpdate]);

    useEffect(() => {
        if (chartRef.current && container.current) {
            chartRef.current.applyOptions(getChartOptions(darkMode, container.current));
        }
    }, [darkMode]);

    // Resize handling
    useEffect(() => {
        if (chartRef.current && container.current) {
            let rafId;
            let resizeTimeout;

            const handleResize = () => {
                if (rafId) {
                    cancelAnimationFrame(rafId);
                }

                rafId = requestAnimationFrame(() => {
                    if (!container.current || !chartRef.current) return;

                    clearTimeout(resizeTimeout);
                    resizeTimeout = setTimeout(() => {
                        if (chartRef.current && container.current) {
                            chartRef.current.timeScale().fitContent();
                            const width = container.current.clientWidth;
                            const height = container.current.clientHeight;

                            chartRef.current.applyOptions({
                                width: width,
                                height: height,
                            });
                        }
                    }, 1);
                });
            };

            handleResize();

            const resizeObserver = new ResizeObserver(() => {
                handleResize();
            });

            resizeObserver.observe(container.current);

            return () => {
                resizeObserver.disconnect();
                clearTimeout(resizeTimeout);
                cancelAnimationFrame(rafId);
            };
        }
    }, [isExpanded, isChatExpanded, isExpandedLeftPanel, isFullscreen]);

    // Imperative handle for AI to add lines
    useImperativeHandle(ref, () => ({
        addLine: (line) => {
            createPriceLine(line.price, line.type, line.text);
        }
    }));

    // Timezone change
    const handleTimezoneChange = useCallback((timezone) => {
        setSelectedTimezone(timezone);
        localStorage.setItem('ntb:copilot:timezone', JSON.stringify(timezone));

        if (originalDataRef.current && candleSeriesRef.current) {
            const adjustedData = convertDataToTimezone(originalDataRef.current, timezone);

            if (candleSeriesRef.current) {
                candleSeriesRef.current.setData(adjustedData);
            }

            if (adjustedData.length > 0) {
                lastDataRef.current = adjustedData[adjustedData.length - 1];
            }
        }
    }, []);

    // Error handlers
    const handleRetry = useCallback(async () => {
        setChartError(null);
        initializeChart(selectedInstrument, selectedTimeframe);
    }, [initializeChart, selectedInstrument, selectedTimeframe]);

    const handleSelectDifferent = useCallback(() => {
        setChartError(null);
    }, []);

    const handleReportIssue = useCallback(() => {
        try {
            const currentUser = auth?.currentUser;
            if (analytics && typeof window !== 'undefined') {
                logEvent(analytics, 'chart_issue_reported', {
                    instrument: selectedInstrument || 'unknown',
                    category: selectedCategory || 'unknown',
                    timeframe: selectedTimeframe || 'unknown',
                    error_code: chartError?.code || null,
                    error_message: chartError?.message || null,
                    uid: currentUser?.uid || 'anonymous',
                    email: currentUser?.email || null
                });
            }
        } catch (eventError) {
            logger.error('Failed to log chart issue event', eventError);
        }

        logger.log('User reported issue:', {
            error: chartError,
            instrument: selectedInstrument,
            category: selectedCategory,
            timeframe: selectedTimeframe
        });
        alert('Thank you for reporting this issue. Our team will look into it.');
    }, [chartError, selectedInstrument, selectedCategory, selectedTimeframe]);

    // Determine provider for legend
    const getProvider = (category) => {
        switch (category) {
            case 'FOREX': return 'OANDA';
            case 'INDICES': return 'OANDA';
            case 'CRYPTO': return 'BINANCE';
            case 'STOCKS': return 'YAHOO';
            default: return '';
        }
    };

    return (
        <div className={`ntc-tradingview-chart-container ${darkMode ? 'ntc-dark-theme' : 'ntc-light-theme'} ${isFullscreen ? 'ntc-fullscreen' : ''}`}>
            {chartError ? (
                <ChartError
                    error={chartError}
                    isDarkMode={darkMode}
                    onRetry={handleRetry}
                    onSelectDifferent={handleSelectDifferent}
                    onReportIssue={handleReportIssue}
                    selectedInstrument={selectedInstrument}
                />
            ) : (
                <>
                    <div className="ntc-chart-header-container">
                        <ChartToolbar
                            darkMode={darkMode}
                            selectedTimeframe={selectedTimeframe}
                            handleTimeframeChange={handleTimeframeChange}
                            handleRefresh={handleRefresh}
                            isControlsDisabled={isControlsDisabled}
                            onFullscreenToggle={handleFullscreenToggle}
                            isFullscreen={isFullscreen}
                            isCreatingTrade={isCreatingTrade}
                            onStartTradeCreation={startTradeCreation}
                            onCancelTradeCreation={cancelTradeCreation}
                            tradeCreationInstruction={getStepInstruction()}
                            isDraggingLine={isDraggingTradeLine}
                            draggingLineName={draggingLine}
                            hasTradeLines={hasTradeLines}
                        />
                    </div>

                    <PriceHeader
                        instrument={selectedInstrument}
                        timeframe={selectedTimeframe}
                        provider={getProvider(selectedCategory)}
                        currentPrice={currentPrice}
                        previousPrice={previousPrice}
                        darkMode={darkMode}
                    />

                    {isLoading && (
                        <ChartSkeleton
                            isDarkMode={darkMode}
                            message={loadingProgress > 0 && loadingProgress < 100
                                ? `Loading chart data... ${Math.round(loadingProgress)}%`
                                : "Loading chart data..."}
                        />
                    )}

                    <div
                        ref={container}
                        className={`ntc-chart-container ${isControlsDisabled ? 'controls-disabled' : ''} ${isLoading ? 'chart-loading' : ''} ${isCreatingTrade ? 'creating-trade' : ''} ${isDraggingTradeLine ? 'dragging-line' : ''}`}
                        style={{
                            position: 'relative',
                            opacity: isLoading ? 0 : 1,
                            transition: 'opacity 0.3s ease-out',
                            cursor: isCreatingTrade ? 'crosshair' : (isDraggingTradeLine ? 'ns-resize' : 'default'),
                        }}
                    />

                    <ChartLegend
                        legendData={legendData}
                        darkMode={darkMode}
                    />

                    {priceLines.length > 0 && (
                        <PriceLinesList
                            priceLines={priceLines}
                            darkMode={darkMode}
                            removePriceLine={removePriceLine}
                            deleteAllLines={deleteAllLines}
                        />
                    )}

                    <TimezoneSelector
                        darkMode={darkMode}
                        selectedTimezone={selectedTimezone}
                        onTimezoneChange={handleTimezoneChange}
                    />
                </>
            )}
        </div>
    );
});

TradingViewLightChart.displayName = 'TradingViewLightChart';

export default TradingViewLightChart;
